import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, CircleOff, Eraser, Palette } from 'lucide-react';
import { useTools } from '../../ToolProvider.jsx';
import { PALETTE, VACIADO_COLOR } from '../../colorPalette.js';
import CustomColorControls from '../CustomColorControls/CustomColorControls.jsx';

// Un objeto con vaciado lleva un patrón por relleno; el color real queda
// guardado aparte (mismo criterio que el panel de capas).
const fillOf = (object) => {
    if (typeof object?._vaciadoFill === 'string') return object._vaciadoFill;
    return typeof object?.fill === 'string' ? object.fill : null;
};

const sameColor = (a, b) => Boolean(a) && Boolean(b) && a.toLowerCase() === b.toLowerCase();

// Las dos opciones que no son un color sino un modo de la pieza, las mismas que
// el selector del panel derecho: el vaciado talla la forma en el fondo en lugar
// de extruirla (rebajado / bajo relieve) y el agujero pasante la atraviesa
// entera. `question` es lo que se pregunta cuando la selección tiene varias
// piezas (sin él, la pregunta es la de pintar).
const SPECIAL_ENTRIES = {
    vaciado: {
        kind: 'vaciado',
        value: VACIADO_COLOR,
        label: 'Vaciado (rebajado)',
        help: 'Vaciado / bajo relieve: la forma se talla en el fondo en lugar de extruirse',
        question: (pieces) => <>¿Vaciar las <strong>{pieces} piezas</strong>?</>,
        Icon: Eraser,
    },
    hole: {
        kind: 'hole',
        label: 'Agujero pasante',
        help: 'Agujero pasante: la forma atraviesa todo el modelo',
        question: (pieces) => <>¿Convertir las <strong>{pieces} piezas</strong> en agujero?</>,
        Icon: CircleOff,
    },
};

// Táctil: mantener pulsado sobre la tira abre una lupa con el color bajo el
// dedo; deslizar sin soltar cambia de color y soltar lo aplica. Moverse antes de
// que cuente como pulsación larga es un desplazamiento normal de la tira.
const LONG_PRESS_MS = 350;
const MOVE_TOLERANCE_PX = 8;
// Soltar a más de esto por encima o por debajo de la tira cancela.
const CANCEL_BAND_PX = 70;
// Tablet (táctil y pantalla alta; un móvil apaisado no llega a 600 px): ahí la
// tira casi cabe entera, así que deslizar por el medio elige color y solo se
// desplaza cerca de los bordes laterales. En el móvil deslizar desplaza.
const TABLET_QUERY = '(pointer: coarse) and (min-height: 600px)';
// Franja de cada lado de la tira que desplaza, y velocidad máxima por fotograma.
const EDGE_ZONE_PX = 56;
const EDGE_SCROLL_MAX_PX = 14;
// La burbuja de la lupa cabe entera: queda a ~3,5 rem sobre el dedo y mide
// ~6 rem de alto, así que más arriba que esto se saldría por el borde de
// la pantalla; y a los lados, media burbuja.
const LOUPE_MIN_TOP_PX = 170;
const LOUPE_SIDE_MARGIN_PX = 64;

const collectLeaves = (object, leaves = []) => {
    if (object?._objects?.length) object._objects.forEach(child => collectLeaves(child, leaves));
    else if (object) leaves.push(object);
    return leaves;
};

/**
 * Cuántas formas acabaría pintando un clic.
 *
 * `setColor` baja hasta las hojas del grupo, así que un SVG importado se
 * repinta entero. El agujero se salta porque el editor tampoco lo pinta.
 */
const countPieces = (object) => {
    if (!object) return 0;
    if (object._objects) return object._objects.reduce((total, child) => total + countPieces(child), 0);
    return String(object.id || '').startsWith('hole') ? 0 : 1;
};

// Identidad estable por objeto: hace falta para distinguir "otra selección"
// de "la misma selección repintada", y fabric no da ningún id propio.
const objectUid = (() => {
    const ids = new WeakMap();
    let next = 0;
    return (object) => {
        if (!ids.has(object)) ids.set(object, ++next);
        return ids.get(object);
    };
})();

/**
 * Colorear la selección desde el lienzo, sin abrir el panel derecho.
 *
 * Vive al lado de los controles de vista y solo aparece cuando hay algo
 * seleccionado: sin selección no hay nada que pintar. Es una prueba de
 * enfoque — el selector del panel derecho sigue estando.
 */
const ColorCarousel = () => {
    const { canvas, onChangeColor, onSetHoleOperation } = useTools() || {};
    const [selection, setSelection] = useState([]);
    const [open, setOpen] = useState(false);
    const [pendingColor, setPendingColor] = useState(null);
    const [confirmedForSelection, setConfirmedForSelection] = useState(false);
    const stripRef = useRef(null);
    const [loupe, setLoupe] = useState(null);
    // Lo último que hace falta dentro de los listeners táctiles, que se
    // enganchan una vez y no ven los renders posteriores.
    const latest = useRef({});

    // La selección se lee del lienzo, que es quien manda, en vez de duplicarla.
    useEffect(() => {
        if (!canvas) return undefined;
        const refresh = () => setSelection(canvas.getActiveObjects() || []);
        refresh();
        const events = [
            'selection:created', 'selection:updated', 'selection:cleared',
            'object:removed', 'layer:changed', 'layer:nested-selection',
        ];
        events.forEach(event => canvas.on(event, refresh));
        return () => events.forEach(event => canvas.off(event, refresh));
    }, [canvas]);

    const signature = selection.map(objectUid).join('|');

    // Cambiar de selección cancela lo que hubiera pendiente y vuelve a exigir
    // confirmación: el permiso se dio para aquellas piezas, no para estas.
    useEffect(() => {
        setPendingColor(null);
        setConfirmedForSelection(false);
    }, [signature]);

    // Al quedarse sin selección el botón desaparece; el carrusel se va con él.
    useEffect(() => {
        if (selection.length === 0) setOpen(false);
    }, [selection.length]);

    useEffect(() => {
        const strip = stripRef.current;
        if (!open || !strip) return undefined;

        // `mode`: qué está haciendo el dedo.
        //   pending → aún no se sabe (toque, pulsación larga o deslizamiento);
        //   pan     → arrastre de la tira, solo en tablet y desde un borde;
        //   scrub   → lupa abierta, eligiendo color.
        const gesture = {
            mode: 'pending', tablet: false, timer: null, raf: null,
            startX: 0, startY: 0, startScroll: 0, x: 0, y: 0, entry: null,
        };
        const maxScroll = () => strip.scrollWidth - strip.clientWidth;
        // Hacia qué borde está el dedo y cuánto (0 = lejos de los dos, 1 = en el
        // mismo borde). Solo cuenta un borde si la tira aún puede ir hacia allí.
        const edgePull = (x) => {
            const rect = strip.getBoundingClientRect();
            const left = (rect.left + EDGE_ZONE_PX - x) / EDGE_ZONE_PX;
            const right = (x - (rect.right - EDGE_ZONE_PX)) / EDGE_ZONE_PX;
            if (left > 0 && strip.scrollLeft > 1) return { direction: -1, strength: Math.min(left, 1) };
            if (right > 0 && strip.scrollLeft < maxScroll() - 1) return { direction: 1, strength: Math.min(right, 1) };
            return { direction: 0, strength: 0 };
        };
        // El color bajo el dedo se busca por la posición horizontal, a la altura
        // del centro de la tira: así sigue acertando aunque el dedo suba un poco.
        const swatchAt = (x, y) => {
            const rect = strip.getBoundingClientRect();
            if (y < rect.top - CANCEL_BAND_PX || y > rect.bottom + CANCEL_BAND_PX) return null;
            const probeX = Math.min(Math.max(x, rect.left + 1), rect.right - 1);
            // Los de la paleta y los que ha añadido el usuario ("Tus colores").
            const node = document.elementFromPoint(probeX, rect.top + rect.height / 2)
                ?.closest?.('[data-palette-index], [data-custom-color]');
            if (!node || !strip.contains(node)) return null;
            if (node.dataset.customColor) {
                return { value: node.dataset.customColor, description: `Tu color ${node.dataset.customColor}` };
            }
            return PALETTE[Number(node.dataset.paletteIndex)];
        };
        const refreshLoupe = () => {
            gesture.entry = swatchAt(gesture.x, gesture.y);
            setLoupe(gesture.entry ? { entry: gesture.entry, x: gesture.x, y: gesture.y } : null);
        };
        // Con la lupa abierta, acercar el dedo a un borde desplaza la tira, más
        // deprisa cuanto más cerca; el color bajo el dedo se va actualizando.
        const edgeScrollLoop = () => {
            if (gesture.mode !== 'scrub') return;
            const { direction, strength } = edgePull(gesture.x);
            if (direction) {
                strip.style.scrollBehavior = 'auto';
                strip.scrollLeft += direction * strength * EDGE_SCROLL_MAX_PX;
                refreshLoupe();
            }
            gesture.raf = requestAnimationFrame(edgeScrollLoop);
        };
        const startScrub = () => {
            const entry = swatchAt(gesture.x, gesture.y);
            if (!entry) return false;
            gesture.mode = 'scrub';
            navigator.vibrate?.(8);
            refreshLoupe();
            gesture.raf = requestAnimationFrame(edgeScrollLoop);
            return true;
        };
        const reset = () => {
            clearTimeout(gesture.timer);
            cancelAnimationFrame(gesture.raf);
            gesture.timer = null;
            gesture.raf = null;
            gesture.mode = 'pending';
            gesture.entry = null;
            strip.style.scrollBehavior = '';
            setLoupe(null);
        };
        const onTouchStart = (event) => {
            reset();
            if (event.touches.length !== 1) return;
            const touch = event.touches[0];
            gesture.tablet = window.matchMedia(TABLET_QUERY).matches;
            Object.assign(gesture, {
                startX: touch.clientX, startY: touch.clientY, x: touch.clientX, y: touch.clientY,
                startScroll: strip.scrollLeft,
            });
            // Tablet: empezar pegado a un borde es arrastrar la tira; en cualquier
            // otro sitio el dedo elige color (la tira no se mueve por el medio).
            if (gesture.tablet && edgePull(touch.clientX).direction) {
                gesture.mode = 'pan';
                return;
            }
            gesture.timer = setTimeout(startScrub, LONG_PRESS_MS);
        };
        const onTouchMove = (event) => {
            const touch = event.touches[0];
            if (!touch) return;
            gesture.x = touch.clientX;
            gesture.y = touch.clientY;
            if (gesture.mode === 'pan') {
                if (event.cancelable) event.preventDefault();
                strip.style.scrollBehavior = 'auto';
                strip.scrollLeft = gesture.startScroll - (touch.clientX - gesture.startX);
                return;
            }
            if (gesture.mode === 'pending') {
                const moved = Math.hypot(touch.clientX - gesture.startX, touch.clientY - gesture.startY);
                if (moved <= MOVE_TOLERANCE_PX) return;
                clearTimeout(gesture.timer);
                gesture.timer = null;
                // Móvil: moverse antes de la pulsación larga es desplazar la tira
                // (lo hace el navegador). Tablet: es elegir color, sin esperar.
                if (!(gesture.tablet && startScrub())) return;
            }
            // Ya con la lupa abierta el gesto es de la lupa, no de la tira.
            if (event.cancelable) event.preventDefault();
            refreshLoupe();
        };
        const onTouchEnd = (event) => {
            const wasScrub = gesture.mode === 'scrub';
            const entry = gesture.entry;
            const wasPan = gesture.mode === 'pan';
            reset();
            if (wasPan) {
                // Un arrastre de la tira no es un toque: que no elija color.
                if (Math.abs(gesture.x - gesture.startX) > MOVE_TOLERANCE_PX && event.cancelable) event.preventDefault();
                return;
            }
            if (!wasScrub) return;
            // Sin esto el navegador manda además un clic al color de debajo.
            event.preventDefault();
            if (entry) latest.current.handleSwatch?.(entry);
        };

        strip.addEventListener('touchstart', onTouchStart, { passive: true });
        strip.addEventListener('touchmove', onTouchMove, { passive: false });
        strip.addEventListener('touchend', onTouchEnd, { passive: false });
        strip.addEventListener('touchcancel', reset);
        return () => {
            strip.removeEventListener('touchstart', onTouchStart);
            strip.removeEventListener('touchmove', onTouchMove);
            strip.removeEventListener('touchend', onTouchEnd);
            strip.removeEventListener('touchcancel', reset);
            reset();
        };
    }, [open, pendingColor]);

    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (event) => {
            if (event.key !== 'Escape') return;
            // Escape deshace un paso: primero la pregunta, luego el carrusel.
            if (pendingColor) setPendingColor(null);
            else setOpen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, pendingColor]);

    if (selection.length === 0) return null;

    const colors = selection.map(fillOf);
    const currentColor = colors.every(color => sameColor(color, colors[0])) ? colors[0] : null;
    const pieces = selection.reduce((total, object) => total + countPieces(object), 0);
    // Un grupo o una selección múltiple se pintan enteros de un clic, y eso
    // aplana un SVG multicolor sin avisar. Con más de una pieza se pregunta.
    const needsConfirm = pieces > 1 && !confirmedForSelection;
    const scrollStrip = (direction) => stripRef.current?.scrollBy({ left: direction * 180, behavior: 'smooth' });

    // Vaciado y agujero solo tienen sentido en dibujos: el fondo tiene su propio
    // selector de color y no se vacía.
    const allowSpecial = !selection.some(object => String(object?.id ?? '').startsWith('background'));
    const isHole = allowSpecial
        && selection.flatMap(object => collectLeaves(object)).every(object => object?._geometryRole === 'hole');
    const isVaciado = allowSpecial && !isHole && sameColor(currentColor, VACIADO_COLOR);

    const applyEntry = (entry) => {
        if (entry.kind === 'hole') onSetHoleOperation?.();
        else onChangeColor?.({ value: entry.value });
    };

    const handleSwatch = (entry) => {
        if (needsConfirm) {
            setPendingColor(entry);
            return;
        }
        applyEntry(entry);
    };

    latest.current.handleSwatch = handleSwatch;

    const confirmPending = () => {
        applyEntry(pendingColor);
        // Dicho que sí una vez, se puede seguir probando colores sobre las
        // mismas piezas sin que pregunte en cada clic.
        setConfirmedForSelection(true);
        setPendingColor(null);
    };

    return (
        <div className="customiz3r-color-controls" aria-label="Color de la selección">
            <button
                type="button"
                className={`btn btn-sm btn-circle tooltip tooltip-top ${open ? 'btn-primary' : 'btn-ghost'}`}
                aria-expanded={open}
                data-tip={pieces === 1 ? 'Color de la selección' : `Color de la selección (${pieces} piezas)`}
                aria-label="Color de la selección"
                onClick={() => setOpen(value => !value)}
            >
                {isHole
                    ? <CircleOff className="h-[1.1rem] w-[1.1rem]" />
                    : isVaciado
                        ? <Eraser className="h-[1.1rem] w-[1.1rem]" />
                        : currentColor
                            ? <span
                                className="h-4 w-4 rounded-full border border-base-content/40"
                                style={{ backgroundColor: currentColor }}
                            />
                            : <Palette className="h-[1.1rem] w-[1.1rem]" />}
            </button>

            {open && (
                <div
                    className={`customiz3r-color-carousel ${pendingColor ? 'is-confirming' : ''}`}
                    role="group"
                    aria-label="Colores disponibles"
                >
                    {pendingColor ? (
                        <div className="customiz3r-color-carousel__confirm">
                            <p className="customiz3r-color-carousel__question text-xs">
                                {pendingColor.kind
                                    ? <pendingColor.Icon className="h-4 w-4 shrink-0" />
                                    : <span
                                        className="customiz3r-color-carousel__swatch shrink-0"
                                        style={{ backgroundColor: pendingColor.value }}
                                    />}
                                <span>
                                    {pendingColor.question
                                        ? pendingColor.question(pieces)
                                        : <>¿Pintar las <strong>{pieces} piezas</strong>?</>}
                                </span>
                            </p>
                            <div className="customiz3r-color-carousel__actions">
                                <button type="button" className="btn btn-xs btn-primary" onClick={confirmPending}>
                                    Sí, todas
                                </button>
                                <button type="button" className="btn btn-xs btn-ghost" onClick={() => setPendingColor(null)}>
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {allowSpecial && (
                                // Fuera de la tira que se desplaza: siempre a la vista, y
                                // sus tooltips no los recorta el scroll.
                                <div className="customiz3r-color-carousel__specials">
                                    {[SPECIAL_ENTRIES.vaciado, SPECIAL_ENTRIES.hole].map((entry) => {
                                        const active = entry.kind === 'hole' ? isHole : isVaciado;
                                        return (
                                            <button
                                                key={entry.kind}
                                                type="button"
                                                className={`btn btn-xs btn-circle tooltip tooltip-top customiz3r-color-carousel__special ${
                                                    active ? (entry.kind === 'hole' ? 'btn-error' : 'btn-primary') : 'btn-ghost'
                                                }`}
                                                data-tip={entry.help}
                                                aria-label={entry.label}
                                                aria-pressed={active}
                                                onClick={() => handleSwatch(entry)}
                                            >
                                                <entry.Icon className="h-4 w-4" />
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                            <button
                                type="button"
                                className="btn btn-xs btn-circle btn-ghost shrink-0"
                                title="Ver colores anteriores"
                                onClick={() => scrollStrip(-1)}
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>

                            <div className="customiz3r-color-carousel__strip" ref={stripRef}>
                                {PALETTE.map((entry, index) => (
                                    <button
                                        // La paleta repite algún valor con distinto nombre
                                        // (Maroon y Burgundy), así que la clave lleva el índice.
                                        key={`${entry.value}-${index}`}
                                        type="button"
                                        className={`customiz3r-color-carousel__swatch ${sameColor(currentColor, entry.value) ? 'is-active' : ''}`}
                                        data-palette-index={index}
                                        style={{ backgroundColor: entry.value }}
                                        title={entry.description}
                                        aria-label={entry.description}
                                        onClick={() => handleSwatch(entry)}
                                    />
                                ))}
                                <CustomColorControls
                                    variant="strip"
                                    activeColor={currentColor}
                                    onPick={(hex) => handleSwatch({ value: hex })}
                                />
                            </div>

                            <button
                                type="button"
                                className="btn btn-xs btn-circle btn-ghost shrink-0"
                                title="Ver más colores"
                                onClick={() => scrollStrip(1)}
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </>
                    )}
                </div>
            )}
            {loupe && createPortal(
                // En el body, no aquí dentro: el dock tiene `backdrop-filter`, que
                // convertiría su `position: fixed` en relativo a la píldora.
                <div
                    className="customiz3r-color-loupe"
                    style={{
                        left: Math.min(Math.max(loupe.x, LOUPE_SIDE_MARGIN_PX), window.innerWidth - LOUPE_SIDE_MARGIN_PX),
                        top: Math.max(loupe.y, LOUPE_MIN_TOP_PX),
                    }}
                    aria-hidden="true"
                >
                    <span className="customiz3r-color-loupe__swatch" style={{ backgroundColor: loupe.entry.value }} />
                    <span className="customiz3r-color-loupe__name">{loupe.entry.description}</span>
                </div>,
                document.body,
            )}
        </div>
    );
};

export default ColorCarousel;
